import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { saveUser } from '../utils/authHelper';
import { FaLock, FaUser, FaShieldAlt, FaEye, FaEyeSlash, FaChevronRight } from 'react-icons/fa';

const API = process.env.REACT_APP_API_URL || 'https://api.antikbet.dev:1881';

/* ── 6 hane OTP ── */
const OtpInput = ({ value, onChange, onComplete }) => {
  const r0=useRef(),r1=useRef(),r2=useRef(),r3=useRef(),r4=useRef(),r5=useRef();
  const refs=[r0,r1,r2,r3,r4,r5];
  const digits = value.split('').concat(Array(6).fill('')).slice(0, 6);

  const handleKey = (i, e) => {
    if (e.key === 'Backspace') {
      onChange(digits.map((d, j) => j === i ? '' : d).join(''));
      if (i > 0) refs[i - 1].current?.focus();
      return;
    }
    if (e.key === 'ArrowLeft'  && i > 0) { refs[i-1].current?.focus(); return; }
    if (e.key === 'ArrowRight' && i < 5) { refs[i+1].current?.focus(); return; }
    if (!/^\d$/.test(e.key)) return;
    const next = digits.map((d, j) => j === i ? e.key : d).join('').slice(0, 6);
    onChange(next);
    if (i < 5) refs[i + 1].current?.focus();
    if (next.replace(/\s/g,'').length === 6) onComplete?.(next);
  };

  const handlePaste = (e) => {
    const p = e.clipboardData.getData('text').replace(/\D/g,'').slice(0,6);
    if (p.length === 6) { onChange(p); onComplete?.(p); refs[5].current?.focus(); }
    e.preventDefault();
  };

  return (
    <div style={{ display:'flex', gap:10, justifyContent:'center' }}>
      {digits.map((d,i) => (
        <input key={i} ref={refs[i]} type="text" inputMode="numeric"
          maxLength={1} value={d} onChange={() => {}}
          onKeyDown={e => handleKey(i,e)} onPaste={handlePaste}
          onFocus={e => e.target.select()}
          style={{
            width:48, height:56, textAlign:'center', borderRadius:14,
            background: d ? 'rgba(242,113,39,.12)' : 'rgba(255,255,255,.04)',
            border:`2px solid ${d ? 'rgba(242,113,39,.5)' : 'rgba(255,255,255,.08)'}`,
            color:'#f0eeff', fontSize:'1.4rem', fontWeight:700,
            fontFamily:"'DM Mono',monospace", outline:'none',
            transition:'all .15s', caretColor:'transparent',
            boxShadow: d ? '0 0 20px rgba(242,113,39,.2)' : 'none',
          }}
        />
      ))}
    </div>
  );
};

/* ── Floating particle ── */
const Particle = ({ style }) => (
  <div style={{ position:'absolute', borderRadius:'50%', pointerEvents:'none', ...style }} />
);

export default function Login() {
  const [step,     setStep]     = useState('credentials');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPw,   setShowPw]   = useState(false);
  const [otp,      setOtp]      = useState('');
  const [busy,     setBusy]     = useState(false);
  const [err,      setErr]      = useState('');
  const [shake,    setShake]    = useState(false);

  const triggerShake = () => { setShake(true); setTimeout(() => setShake(false), 500); };

  const finishLogin = (data) => {
    saveUser(data.user, data.token);
    const dest = data.needsSetup2FA ? '/setup-2fa' : '/dashboard';
    window.location.replace(dest);
  };

  const submitCredentials = async (e) => {
    e?.preventDefault();
    if (!username.trim() || !password.trim()) { setErr('Kullanıcı adı ve şifre gerekli.'); triggerShake(); return; }
    setErr(''); setBusy(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { username: username.trim(), password });
      if (res.data.require2FA) { setStep('2fa'); setBusy(false); return; }
      finishLogin(res.data);
    } catch (e) {
      setErr(e.response?.data?.message || 'Giriş yapılamadı.');
      triggerShake(); setBusy(false);
    }
  };

  const submitOtp = async (code) => {
    const cleaned = (code || otp).replace(/\s/g,'');
    if (cleaned.length !== 6) { setErr('6 haneli kodu eksiksiz girin.'); triggerShake(); return; }
    setErr(''); setBusy(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { username: username.trim(), password, totpCode: cleaned });
      finishLogin(res.data);
    } catch (e) {
      setErr(e.response?.data?.message || 'Kod hatalı veya süresi dolmuş.');
      setOtp(''); triggerShake(); setBusy(false);
    }
  };

  return (
    <div style={{
      minHeight:'100vh', background:'#08090b',
      display:'flex', alignItems:'center', justifyContent:'center',
      fontFamily:"'Outfit',sans-serif", overflow:'hidden', position:'relative',
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=DM+Mono:wght@400;500&display=swap');
        *{box-sizing:border-box;margin:0;padding:0;}
        input:-webkit-autofill{-webkit-box-shadow:0 0 0 100px #111215 inset!important;-webkit-text-fill-color:#f0eeff!important;}
        @keyframes floatA{0%,100%{transform:translateY(0) rotate(0deg)}50%{transform:translateY(-20px) rotate(5deg)}}
        @keyframes floatB{0%,100%{transform:translateY(0) rotate(0deg)}50%{transform:translateY(15px) rotate(-3deg)}}
        @keyframes fadeUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
        @keyframes shake{0%,100%{transform:translateX(0)}20%{transform:translateX(-8px)}40%{transform:translateX(8px)}60%{transform:translateX(-6px)}80%{transform:translateX(6px)}}
        @keyframes pulse{0%,100%{opacity:.6}50%{opacity:1}}
        @keyframes spin{to{transform:rotate(360deg)}}
        .card-enter{animation:fadeUp .4s ease forwards;}
        .shake{animation:shake .4s ease;}
      `}</style>

      {/* Background glow */}
      <div style={{ position:'absolute', inset:0, overflow:'hidden', pointerEvents:'none' }}>
        <div style={{ position:'absolute', width:600, height:600, borderRadius:'50%', background:'radial-gradient(circle,rgba(242,113,39,.07) 0%,transparent 70%)', top:'50%', left:'50%', transform:'translate(-50%,-60%)' }} />
        <div style={{ position:'absolute', width:400, height:400, borderRadius:'50%', background:'radial-gradient(circle,rgba(167,139,250,.05) 0%,transparent 70%)', bottom:'-10%', right:'-5%' }} />
        {/* Grid lines */}
        <div style={{ position:'absolute', inset:0, backgroundImage:'linear-gradient(rgba(255,255,255,.015) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.015) 1px,transparent 1px)', backgroundSize:'60px 60px' }} />
        {/* Floating orbs */}
        <Particle style={{ width:8, height:8, background:'rgba(242,113,39,.4)', top:'20%', left:'15%', animation:'floatA 6s ease-in-out infinite', boxShadow:'0 0 16px rgba(242,113,39,.5)' }} />
        <Particle style={{ width:5, height:5, background:'rgba(167,139,250,.5)', top:'70%', left:'80%', animation:'floatB 8s ease-in-out infinite', boxShadow:'0 0 12px rgba(167,139,250,.6)' }} />
        <Particle style={{ width:4, height:4, background:'rgba(242,113,39,.3)', top:'40%', right:'20%', animation:'floatA 7s ease-in-out infinite 1s', boxShadow:'0 0 10px rgba(242,113,39,.4)' }} />
        <Particle style={{ width:6, height:6, background:'rgba(96,165,250,.3)', bottom:'25%', left:'25%', animation:'floatB 9s ease-in-out infinite 2s' }} />
      </div>

      {/* Card */}
      <div className={shake ? 'shake' : ''} style={{
        width:'100%', maxWidth:420, position:'relative', zIndex:1,
        padding: '0 20px',
      }}>

        {/* Logo area */}
        <div style={{ textAlign:'center', marginBottom:32, animation:'fadeUp .5s ease' }}>
          <div style={{
            width:64, height:64, borderRadius:20, margin:'0 auto 16px',
            background:'linear-gradient(135deg,#f27127 0%,#a83300 100%)',
            display:'flex', alignItems:'center', justifyContent:'center',
            boxShadow:'0 0 0 1px rgba(242,113,39,.3), 0 0 40px rgba(242,113,39,.25), 0 16px 40px rgba(0,0,0,.4)',
            position:'relative',
          }}>
            <div style={{ position:'absolute', inset:-1, borderRadius:21, background:'linear-gradient(135deg,rgba(255,255,255,.15),transparent)', pointerEvents:'none' }} />
            <span style={{ color:'#fff', fontWeight:900, fontSize:'1.6rem', letterSpacing:'-1px' }}>Y</span>
          </div>
          <h1 style={{ color:'#f0eeff', fontWeight:800, fontSize:'1.25rem', letterSpacing:'-.3px' }}>Yönetim Paneli</h1>
          <p style={{ color:'rgba(240,238,255,.3)', fontSize:'.73rem', marginTop:5, letterSpacing:'.5px', textTransform:'uppercase' }}>
            {step === 'credentials' ? 'Güvenli Giriş' : 'İki Faktörlü Doğrulama'}
          </p>
        </div>

        {/* Main card */}
        <div style={{
          background:'rgba(255,255,255,.03)', borderRadius:24,
          border:'1px solid rgba(255,255,255,.07)',
          boxShadow:'0 32px 80px rgba(0,0,0,.6), inset 0 1px 0 rgba(255,255,255,.06)',
          overflow:'hidden', position:'relative',
        }}>
          {/* Top accent line */}
          <div style={{ height:2, background:'linear-gradient(90deg,transparent,rgba(242,113,39,.6),transparent)' }} />

          <div className="card-enter" key={step} style={{ padding:'32px 28px 28px' }}>

            {/* ── CREDENTIALS ── */}
            {step === 'credentials' && (
              <>
                {err && (
                  <div style={{ marginBottom:18, padding:'11px 14px', borderRadius:12, background:'rgba(239,68,68,.08)', border:'1px solid rgba(239,68,68,.2)', color:'#fca5a5', fontSize:'.78rem', display:'flex', alignItems:'center', gap:8 }}>
                    <span style={{ width:6, height:6, borderRadius:'50%', background:'#f87171', flexShrink:0, animation:'pulse 1.5s infinite' }} />
                    {err}
                  </div>
                )}

                <form onSubmit={submitCredentials}>
                  {/* Username */}
                  <div style={{ marginBottom:14 }}>
                    <label style={{ fontSize:'.6rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'1.5px', color:'rgba(240,238,255,.3)', display:'block', marginBottom:7 }}>Kullanıcı Adı</label>
                    <div style={{ position:'relative' }}>
                      <FaUser style={{ position:'absolute', left:14, top:'50%', transform:'translateY(-50%)', color:'rgba(255,255,255,.18)', fontSize:'.7rem' }} />
                      <input
                        type="text" value={username} onChange={e=>setUsername(e.target.value)}
                        placeholder="Kullanıcı Adı" autoFocus autoComplete="username"
                        style={{ width:'100%', padding:'12px 14px 12px 38px', borderRadius:13, background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.08)', color:'#f0eeff', fontSize:'.85rem', fontFamily:"'Outfit',sans-serif", outline:'none', transition:'border .2s' }}
                        onFocus={e=>e.target.style.borderColor='rgba(242,113,39,.4)'}
                        onBlur={e=>e.target.style.borderColor='rgba(255,255,255,.08)'}
                      />
                    </div>
                  </div>

                  {/* Password */}
                  <div style={{ marginBottom:24 }}>
                    <label style={{ fontSize:'.6rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'1.5px', color:'rgba(240,238,255,.3)', display:'block', marginBottom:7 }}>Şifre</label>
                    <div style={{ position:'relative' }}>
                      <FaLock style={{ position:'absolute', left:14, top:'50%', transform:'translateY(-50%)', color:'rgba(255,255,255,.18)', fontSize:'.7rem' }} />
                      <input
                        type={showPw?'text':'password'} value={password}
                        onChange={e=>setPassword(e.target.value)}
                        placeholder="••••••••" autoComplete="current-password"
                        style={{ width:'100%', padding:'12px 40px 12px 38px', borderRadius:13, background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.08)', color:'#f0eeff', fontSize:'.85rem', fontFamily:"'Outfit',sans-serif", outline:'none', transition:'border .2s' }}
                        onFocus={e=>e.target.style.borderColor='rgba(242,113,39,.4)'}
                        onBlur={e=>e.target.style.borderColor='rgba(255,255,255,.08)'}
                      />
                      <button type="button" onClick={()=>setShowPw(s=>!s)} style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:showPw?'rgba(242,113,39,.8)':'rgba(255,255,255,.2)', fontSize:'.8rem', display:'flex', padding:3 }}>
                        {showPw?<FaEyeSlash/>:<FaEye/>}
                      </button>
                    </div>
                  </div>

                  <button type="submit" style={{
                    width:'100%', padding:'13px', borderRadius:14, border:'none',
                    cursor:busy?'not-allowed':'pointer',
                    background:busy?'rgba(242,113,39,.3)':'linear-gradient(135deg,#f27127,#c44b0a)',
                    color:'#fff', fontSize:'.88rem', fontWeight:700,
                    fontFamily:"'Outfit',sans-serif",
                    boxShadow:busy?'none':'0 4px 24px rgba(242,113,39,.35)',
                    opacity:busy?.7:1, transition:'all .2s',
                    display:'flex', alignItems:'center', justifyContent:'center', gap:8,
                  }}>
                    {busy ? (
                      <div style={{ width:16, height:16, border:'2px solid rgba(255,255,255,.3)', borderTopColor:'#fff', borderRadius:'50%', animation:'spin .7s linear infinite' }} />
                    ) : (
                      <><span>Giriş Yap</span><FaChevronRight style={{ fontSize:'.65rem' }} /></>
                    )}
                  </button>
                </form>
              </>
            )}

            {/* ── 2FA OTP ── */}
            {step === '2fa' && (
              <>
                <div style={{ textAlign:'center', marginBottom:28 }}>
                  <div style={{ width:52, height:52, borderRadius:16, margin:'0 auto 14px', background:'rgba(242,113,39,.08)', border:'1px solid rgba(242,113,39,.2)', display:'flex', alignItems:'center', justifyContent:'center', color:'#f27127', fontSize:'1.15rem' }}>
                    <FaShieldAlt />
                  </div>
                  <p style={{ color:'rgba(240,238,255,.5)', fontSize:'.76rem', lineHeight:1.7 }}>
                    Google Authenticator uygulamasındaki<br/>6 haneli doğrulama kodunu girin
                  </p>
                </div>

                {err && (
                  <div style={{ marginBottom:20, padding:'11px 14px', borderRadius:12, background:'rgba(239,68,68,.08)', border:'1px solid rgba(239,68,68,.2)', color:'#fca5a5', fontSize:'.78rem', textAlign:'center', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                    <span style={{ width:6, height:6, borderRadius:'50%', background:'#f87171', flexShrink:0 }} />
                    {err}
                  </div>
                )}

                <OtpInput value={otp} onChange={setOtp} onComplete={submitOtp} />

                <button onClick={()=>submitOtp()} style={{
                  width:'100%', padding:'13px', borderRadius:14, border:'none', marginTop:22,
                  cursor:(otp.length<6||busy)?'not-allowed':'pointer',
                  background:otp.replace(/\s/g,'').length===6?'linear-gradient(135deg,#f27127,#c44b0a)':'rgba(255,255,255,.06)',
                  color:otp.replace(/\s/g,'').length===6?'#fff':'rgba(255,255,255,.3)',
                  fontSize:'.88rem', fontWeight:700, fontFamily:"'Outfit',sans-serif",
                  boxShadow:otp.replace(/\s/g,'').length===6?'0 4px 24px rgba(242,113,39,.35)':'none',
                  opacity:busy?.7:1, transition:'all .25s',
                  display:'flex', alignItems:'center', justifyContent:'center', gap:8,
                }}>
                  {busy ? (
                    <div style={{ width:16, height:16, border:'2px solid rgba(255,255,255,.3)', borderTopColor:'#fff', borderRadius:'50%', animation:'spin .7s linear infinite' }} />
                  ) : 'Doğrula ve Giriş Yap'}
                </button>

                <button onClick={()=>{setStep('credentials');setErr('');setOtp('');}} style={{ width:'100%', marginTop:10, padding:'10px', borderRadius:13, border:'1px solid rgba(255,255,255,.05)', background:'transparent', color:'rgba(240,238,255,.3)', fontSize:'.78rem', fontWeight:500, cursor:'pointer', fontFamily:"'Outfit',sans-serif", transition:'all .15s' }}
                  onMouseEnter={e=>e.target.style.color='rgba(240,238,255,.6)'}
                  onMouseLeave={e=>e.target.style.color='rgba(240,238,255,.3)'}
                >
                  ← Geri Dön
                </button>
              </>
            )}
          </div>

          {/* Bottom bar */}
          <div style={{ padding:'12px 28px', borderTop:'1px solid rgba(255,255,255,.04)', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
            <span style={{ width:6, height:6, borderRadius:'50%', background:'#10b981', boxShadow:'0 0 8px #10b981', animation:'pulse 2s infinite' }} />
            <span style={{ fontSize:'.65rem', color:'rgba(240,238,255,.2)', letterSpacing:'.5px' }}>256-BIT SSL GÜVENLİ BAĞLANTI</span>
          </div>
        </div>

        <p style={{ textAlign:'center', marginTop:20, fontSize:'.63rem', color:'rgba(255,255,255,.1)' }}>
          Yönetim Paneli · Yetkisiz erişim yasaktır
        </p>
      </div>
    </div>
  );
}
